import {
    bootstrap as sharedBootstrap,
    BootstrapOptions as SharedBootstrapOptions,
} from 'owa-shared-bootstrap';
// Import setHtmlLangOrchestrator so that it gets run.
import './orchestrators/setHtmlLangOrchestrator';
// Need to wait for flags before initializing themes
// since themes depend on flags.
import { waitForFlagsInitialization } from './orchestrators/waitForFlagsInitialization';
import 'owa-fabric-theme'; // Need to initialize orchestrators that listen for theme and locale change
import { DesktopErrorComponentAsync } from 'owa-desktop-error-component';
import { isOpx, getOpxHostApi } from 'owa-opx';
import { getWindowTitle } from './initialization/getWindowTitle';
import { initializeOwaLocalization } from './initialization/initializeOwaLocalization';
import initializeTheme from './initialization/initializeTheme';
import { getBposPromise } from 'owa-bpos-store';
import { lazyPwaUtils } from 'owa-pwa-utils';
import { updateServiceConfig } from 'owa-service/lib/config';
import { getMailboxSpecificRequestOptions } from 'owa-request-options';
import { isPwa } from 'owa-config';

export interface BootstrapOptions
    extends Omit<
        SharedBootstrapOptions,
        'getWindowTitle' | 'initializeLocalization' | 'fullErrorComponent'
    > {
    getModuleName: () => string;
}

async function bootstrap({ getModuleName, ...options }: BootstrapOptions) {
    const prerenderPromises = (options.prerenderPromises || []).concat({
        promise: waitForFlagsInitialization
            .then(() => options.initialConfig.sessionDataPromise)
            .then(async sessionData => {
                if (isPwa()) {
                    await lazyPwaUtils.import();
                }
                return initializeTheme(sessionData.owaUserConfig);
            }),
        key: 'th',
    });
    const bposPromise = getBposPromise();
    if (bposPromise) {
        prerenderPromises.push({ promise: bposPromise, key: 'bpos' });
    }

    updateServiceConfig({
        // This is used in owa-service to get Request Options with appropriate headers
        // needed for multi-account support. Parameters: UserIdentity, MailboxSmtpAddress and MailboxType
        // passed within getMailboxSpecificRequestOptions are used to decide which headers to be added for the same.
        prepareRequestOptions: getMailboxSpecificRequestOptions,
    });

    return sharedBootstrap({
        ...options,
        getWindowTitle: () => (isOpx() ? getModuleName() : getWindowTitle(getModuleName)),
        initializeLocalization: initializeOwaLocalization,
        fullErrorComponent: DesktopErrorComponentAsync,
        prerenderPromises,
        onComponentError: async () => {
            if (isOpx()) {
                (await getOpxHostApi()).onFatalError();
            }
        },
    });
}

export { bootstrap };
